<script lang="ts">
  import { onMount } from 'svelte';
  import {
    select,
    line,
    axisLeft,
    symbolSquare,
    symbolCircle,
    symbolTriangle,
    axisBottom,
    timeParse,
    scaleLinear,
    Selection,
    symbol,
    extent,
    scaleTime,
    timeDay,
    ScaleTime,
    Axis,
    NumberValue,
    ScaleLinear,
  } from 'd3';
  import type { LineData, LinePoint, SymbolType } from './line-chart.types';
  import { EventHandler } from '../../utilities/EventHandler';

  export let data = '';
  export let backgroundcolor = '#fff';
  export let height = 300;
  export let ticknumbers = 5;

  const parseTime = timeParse('%d-%m-%Y');

  let _data: string;
  let hasMounted = false;
  let eventHandler = new EventHandler();
  let parsedData: LineData[];

  $: parsedData = data ? (JSON.parse(data) as LineData[]) : ([] as LineData[]);
  $: if (data.length > 0 && hasMounted) {
    if (!_data) {
      render();
      _data = data;
    } else if (data !== _data) {
      updateAxes(parsedData);
      parsedData.forEach((data, i) => updateLine(data, i));
      _data = data;
    }
  }

  let chart: HTMLDivElement;
  let svg: Selection<SVGGElement, LinePoint, any, any>;

  let x: ScaleTime<number, number, never>,
    y: ScaleLinear<number, number, never>,
    xAxis: Axis<Date | NumberValue>,
    yAxis: Axis<number | NumberValue>,
    linesD3: Selection<SVGPathElement, LinePoint, any, any>[] = [];

  const margin = { top: 20, right: 40, bottom: 40, left: 60, yAxisPadding: 20 };
  let tooltipData = {
    date: '',
    value: 0,
    show: false,
    customtooltip: '',
  };

  function getChartWidth() {
    let chartWidth = select(chart).style('width');
    if (chartWidth === '' || !chartWidth) {
      chartWidth = `${window.innerWidth / 2}`;
    }
    return parseInt(chartWidth);
  }

  function appendSymbols(
    data: LinePoint[],
    color: string,
    symbolType: SymbolType,
    i: number,
  ) {
    let symbolPath = symbol(symbolSquare);
    switch (symbolType) {
      case 'circle':
        symbolPath = symbol(symbolCircle);
        break;
      case 'triangle':
        symbolPath = symbol(symbolTriangle);
        break;
    }

    svg
      .selectAll()
      .data(data)
      .join('path')
      .attr('d', symbolPath.size(50))
      .attr('opacity', 1)
      .attr(
        'transform',
        (d) => `translate(${x(+d.time) + margin.yAxisPadding},${y(+d.value)})`,
      )
      .attr('class', `chart__symbol-line-${i}`)
      .style('fill', color)
      .style('cursor', 'pointer')
      .on('mouseover', handleTickMouseOver)
      .on('mouseout', () => (tooltipData.show = false))
      .on('click', (_e: MouseEvent, point: LinePoint) => {
        eventHandler.dispatch('point-clicked', point);
      });
  }

  function handleTickMouseOver(_e: MouseEvent, point: LinePoint): void {
    const date = new Date(point.time).toDateString();
    const value = point.value;
    tooltipData = {
      date,
      value: parseInt(value),
      show: true,
      customtooltip: point.customtooltip,
    };
  }

  function updatePath(
    lineD3: Selection<SVGPathElement, LinePoint, any, any>,
    points: LinePoint[],
    color: string,
    strokeDasharray?: number,
  ) {
    lineD3
      .datum(points)
      .attr(
        'd',
        line<LinePoint>()
          .x((d) => x(+d.time))
          .y((d) => y(+d.value)),
      )
      .attr('stroke', color)
      .style('stroke-width', 2.5)
      .style('fill', 'none')
      .attr('transform', `translate(${margin.yAxisPadding},0)`)
      .attr('stroke-dasharray', strokeDasharray || 0);
  }

  function updateAxes(data: LineData[]) {
    const width = getChartWidth();
    const mergedLineData = data.flatMap((d) => d.points);
    const [yMin = 0, yMax = 0] = extent(mergedLineData, (d) => Number(d.value));
    const xDomain = extent(mergedLineData, (d) => parseTime(d.time as string));
    x.domain(xDomain as Iterable<Date | NumberValue>);
    svg
      .selectAll('.x-axis')
      .attr(
        'transform',
        `translate(${margin.yAxisPadding},${height + margin.bottom / 2})`,
      )
      .call(xAxis as any)
      .attr('stroke-width', '0');

    y.domain([yMin, yMax]);
    svg
      .selectAll('.y-axis')
      .call(yAxis as any)
      .select('.domain')
      .attr('stroke-width', '0');

    svg
      .selectAll('.y-axis .tick line')
      .attr('x1', margin.yAxisPadding / 2)
      .attr('x2', width - margin.left)
      .attr('stroke', '#EEEDEA')
      .attr('stroke-width', '1.4px');
  }

  function render(resize?: boolean) {
    const width = getChartWidth();

    x = scaleTime().range([0, width - margin.left - margin.right]);
    xAxis = axisBottom(x)
      .tickSize(0)
      .ticks(timeDay)
      .ticks(ticknumbers)
      .tickPadding(5);

    y = scaleLinear().range([height, 0]);
    yAxis = axisLeft(y).ticks(ticknumbers);

    if (!resize) {
      svg = select<HTMLElement, LinePoint>(chart)
        .append('svg')
        .attr('width', width)
        .attr('height', height + margin.top + margin.bottom)
        .attr('id', 'svg-chart')
        .append('g')
        .attr(
          'transform',
          `translate(${margin.left / 2 + margin.right / 2},${margin.top})`,
        );

      svg
        .append('g')
        .attr('transform', 'translate(0,' + String(height) + ')')
        .attr('class', 'x-axis');
      svg.append('g').attr('class', 'y-axis');
    }

    updateAxes(parsedData);
    parsedData.forEach((data, i) => {
      if (!resize) {
        linesD3.push(svg.append('g').append('path'));
      }
      updateLine(data, i);
    });
  }

  function updateLine(data: LineData, i: number) {
    const points = data.points.map((d) => ({
      time: parseTime(d.time as string) || '',
      value: d.value,
      customtooltip: d.customtooltip,
    }));
    updatePath(linesD3[i], points, data.color, data.strokeDasharray);
    svg.selectAll(`.chart__symbol-line-${i}`).remove();
    appendSymbols(points, data.color, data.symbol, i);
  }

  function resize() {
    if (_data && hasMounted) {
      const width = getChartWidth();
      chart.querySelector('#svg-chart')?.setAttribute('width', `${width}px`);
      render(true);
    }
  }

  onMount(() => {
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(chart);
    hasMounted = true;
  });
</script>

<div
  bind:this={chart}
  class="chart"
  style="background-color: {backgroundcolor};"
>
  {#if tooltipData.show}
    <div
      class="tooltip"
      style={`right: ${margin.right}px; top: ${margin.top}px;`}
    >
      {#if tooltipData.customtooltip}
        {tooltipData.customtooltip}
      {:else}
        {tooltipData.date}: <b>{tooltipData.value}</b>
      {/if}
    </div>
  {/if}
</div>

<div class="legend">
  {#each parsedData as d}
    <div class="legend__item">
      <svg class="legend__box">
        <rect x="0" y="0" width="24" height="16" fill={d.color} />
      </svg>
      <span class="legend__title">{d.title}</span>
    </div>
  {/each}
</div>

<text style="display: none;" />

<style lang="scss">
  @import '@mozaic-ds/styles/settings-tools/_all-settings';

  .legend {
    margin-top: magic-unit-rem(1);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 magic-unit-rem(1.5);
    height: 100%;
    color: $color-grey-999;
    gap: magic-unit-rem(1);

    &__item {
      display: flex;
      justify-content: center;
      font-size: $size-font-04;
      align-items: center;
    }

    &__title {
      white-space: nowrap;
      margin: 0;
    }

    &__box {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: magic-unit-rem(0.25);
      height: 16px;
      width: 24px;
    }
  }

  text {
    font-family: 'LeroyMerlin', sans-serif;
    font-size: 14px;
    color: #555550;
  }

  .tooltip {
    background-color: $color-grey-700;
    border-radius: 4px;
    color: $color-grey-000;
    text-align: center;
    padding: magic-unit-rem(0.5) magic-unit-rem(1);
    top: 0;
    position: absolute;
  }
</style>
